import React, { useEffect, useRef, useState } from 'react';
import { Modal, Box, Text, Group, Switch, Paper } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IProduct } from '@/types/Product.model';
import { userPath, cdnCompanyLogos, networkUrl, websiteDomain, clientRoutes } from '@/ApiPaths';
import { navigateSameWindowOrOpenTab } from '@/services/Utility';
import * as modelViewer from '@google/model-viewer';
import { QRCode } from 'react-qrcode-logo';
import { UserProfile } from '@/store/Store';
import { getJson } from '@/services/httpClient';
import '@/pages/tempViewer.css';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import { useARButtonVisibility } from '@/hooks/useARButtonVisibility';
import { MODEL_VIEWER_PRESENTATION_ATTRS } from '@/constants/modelViewerPresentation';
import { MODEL_VIEWER_RELAXED_ZOOM_ATTRS } from '@/utils/annotationConstants';
import { useModelViewerDynamicZoom } from '@/hooks/useModelViewerDynamicZoom';
import { glbZoomSrc, productGlbUrl, productUsdzUrl } from '@/utils/productModelUrl';
import { ModelViewerResetZoomButton } from '@/components/ModelViewer/ModelViewerResetZoomButton';
import { ModelViewerScreenshotButton } from '@/components/ModelViewer/ModelViewerScreenshotButton';
import {
  VIEWER_CONTROL_CHROME_HOVER_BG,
  VIEWER_CONTROL_CHROME_STYLE,
} from '@/components/ModelViewer/viewerControlChrome';
import { IconEye, Icon3dCubeSphere, IconX } from '@tabler/icons-react';
import {
  ListingStatusBadge,
  ArticleNumberBadge,
  ProductCardOverlayChip,
} from '@/components/badges';
import { EditorChipButton } from '@/components/EditorChipButton';
import productModalClasses from '@/pages/DashboardProductModal.module.css';
import type { IPopulatedListing } from '@/types/Listing.model';

interface AnnotationProductModalProps {
  opened: boolean;
  onClose: () => void;
  product: IProduct | null;
  portalTarget?: HTMLElement | string;
  modalZIndex?: number;
}

/**
 * Modal component for displaying annotation product in 3D
 * Shows the selected product's 3D model in a centered modal
 */
export function AnnotationProductModal({
  opened,
  onClose,
  product,
  portalTarget,
  modalZIndex,
}: AnnotationProductModalProps) {
  const { t } = useTranslation();
  const modelViewerRef = useRef<modelViewer.ModelViewerElement>(null);
  const annotationProductGlbSrc = glbZoomSrc(product, product?._id, opened);
  useModelViewerDynamicZoom(modelViewerRef, annotationProductGlbSrc);
  const deviceInfo = useDeviceDetection();
  const isDesktop = deviceInfo.isDesktop;
  const [showQRCode, setShowQRCode] = useState(false);
  const [showARInfo, setShowARInfo] = useState(false);
  const [productOwner, setProductOwner] = useState<Partial<UserProfile>>();
  const [qrStyle, setQrStyle] = useState<'dots' | 'squares'>('squares');

  const productListings = (product as IProduct & { listings?: IPopulatedListing[] })?.listings;

  useEffect(() => {
    if (product?.owningUser_id) {
      getJson<Partial<UserProfile>>(userPath + product.owningUser_id)
        .then((userData) => setProductOwner(userData))
        .catch((error) => console.error('Error fetching product owner:', error));
    }
  }, [product?.owningUser_id]);

  useARButtonVisibility({ modelViewerRef, isDesktop });

  const handleARClick = () => {
    if (deviceInfo.isDesktop) {
      setShowARInfo(false);
      setShowQRCode(true);
    }
  };

  const corporateColor = productOwner?.qrCodeColor || DEFAULT_QR_CODE_COLOR;

  if (!opened || !product) {
    return null;
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={product.productName || ''}
      size="xl"
      zIndex={modalZIndex}
      portalProps={portalTarget ? { target: portalTarget } : undefined}
      styles={{
        content: {
          padding: 0,
          overflow: 'hidden',
        },
        header: {
          position: 'absolute',
          top: 0,
          right: 0,
          left: 'auto',
          width: 'auto',
          padding: '10px 12px',
          margin: 0,
          minHeight: 'unset',
          background: 'transparent',
          borderBottom: 'none',
          zIndex: 30,
        },
        title: {
          display: 'none',
        },
        body: {
          padding: 0,
        },
      }}
      centered
      radius="md"
      shadow="xl"
      fullScreen={window.innerWidth < 768}
    >
      <Paper radius={0} shadow="none" className={productModalClasses.modalHeader}>
        <div className={productModalClasses.modalHeaderTop}>
          <h2 className={productModalClasses.modalTitle}>{product.productName}</h2>
          <ProductCardOverlayChip
            variant="views"
            icon={<IconEye size={9} stroke={2} />}
            className={productModalClasses.modalViewsChip}
          >
            {product.views || 0} {t('dashboard.metrics.clicks')}
          </ProductCardOverlayChip>
        </div>

        {(product.articleNumber || productListings) && (
          <div className={productModalClasses.modalMetaRow}>
            {product.articleNumber && (
              <ArticleNumberBadge articleNumber={product.articleNumber} appearance="card" />
            )}
            {productListings && (
              <ListingStatusBadge listings={productListings} appearance="card" />
            )}
          </div>
        )}
      </Paper>

      <div className={productModalClasses.modalBody}>
        {product.availableFormats?.includes('.glb') && (
          <Box style={{ position: 'relative' }}>
            <ModelViewerResetZoomButton modelViewerRef={modelViewerRef} />
            <ModelViewerScreenshotButton
              modelViewerRef={modelViewerRef}
              fileName={product.productName}
            />
            <model-viewer
              ref={modelViewerRef}
              style={{
                width: '100%',
                height: '500px',
                overflow: 'hidden',
              }}
              src={productGlbUrl(product._id)}
              ios-src={
                product.availableFormats.includes('.usdz')
                  ? productUsdzUrl(product._id)
                  : ''
              }
              ar
              ar-modes="webxr scene-viewer quick-look"
              ar-scale="auto"
              ar-placement="floor"
              interaction-prompt="auto"
              interaction-prompt-style="basic"
              controls="play pause"
              {...MODEL_VIEWER_PRESENTATION_ATTRS}
              camera-controls
              {...MODEL_VIEWER_RELAXED_ZOOM_ATTRS}
              touch-action="pan-y"
              enable-pan
              autoPlay
            />
            {isDesktop && (
              <button
                type="button"
                onClick={handleARClick}
                style={{
                  ...VIEWER_CONTROL_CHROME_STYLE,
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  padding: 0,
                  color: corporateColor,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transform: 'scale(1)',
                  position: 'absolute',
                  bottom: '16px',
                  right: '16px',
                  zIndex: 11,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.backgroundColor = VIEWER_CONTROL_CHROME_HOVER_BG;
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.backgroundColor = VIEWER_CONTROL_CHROME_STYLE.backgroundColor;
                  e.currentTarget.style.boxShadow = VIEWER_CONTROL_CHROME_STYLE.boxShadow;
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.95)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                className="desktop-ar-buttons"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27,6.96 12,12.01 20.73,6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
              </button>
            )}
          </Box>
        )}

        <Box px="md" py="lg" className={productModalClasses.modalFooter}>
          <Group justify="flex-end" gap="sm" wrap="wrap">
            <EditorChipButton
              variant="primary"
              accentColor={corporateColor}
              leftIcon={<Icon3dCubeSphere size={15} stroke={1.75} />}
              onClick={() => {
                if (!product._id) return;
                const url = `${websiteDomain}${clientRoutes.product}`.replace(
                  ':id',
                  product._id as string
                );
                navigateSameWindowOrOpenTab(url);
              }}
            >
              {t('dashboard.actions.viewProduct')}
            </EditorChipButton>

            <EditorChipButton
              accentColor={corporateColor}
              leftIcon={<IconX size={15} stroke={1.75} />}
              onClick={onClose}
            >
              {t('dashboard.actions.close')}
            </EditorChipButton>
          </Group>
        </Box>
      </div>

      <Modal
        opened={showQRCode}
        onClose={() => {
          setShowQRCode(false);
          setShowARInfo(false);
        }}
        title={<div style={{ display: 'none' }}></div>}
        centered
        size="md"
        radius="lg"
        padding="md"
        zIndex={modalZIndex != null ? modalZIndex + 1 : undefined}
        portalProps={portalTarget ? { target: portalTarget } : undefined}
        overlayProps={{
          blur: 3,
        }}
        styles={{
          content: {
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          },
          header: {
            padding: 0,
            marginBottom: 0,
            marginRight: 12,
          },
          close: {
            marginRight: 10,
          },
          body: {
            paddingTop: 5,
          },
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Text
            fw={600}
            size="lg"
            tt="uppercase"
            mb={15}
            style={{
              color: corporateColor,
              letterSpacing: '1px',
              textAlign: 'center',
              position: 'relative',
              paddingBottom: '10px',
            }}
          >
            <span style={{ position: 'relative', zIndex: 1 }}>{t('marketplace.qr.scan')}</span>
            <span
              style={{
                position: 'absolute',
                height: '3px',
                width: '40px',
                backgroundColor: corporateColor,
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                borderRadius: '2px',
              }}
            ></span>
          </Text>

          {showARInfo && (
            <Text size="sm" mb={10} ta="center" c="dimmed" fw={600}>
              {t('arOnlyMobile')}
            </Text>
          )}

          <Text
            size="sm"
            mb={15}
            c="dimmed"
            ta="center"
            style={{
              maxWidth: '90%',
              lineHeight: 1.4,
            }}
          >
            {t('marketplace.qr.description')}
          </Text>

          <div
            style={{
              padding: '15px',
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              borderRadius: '12px',
              marginBottom: '15px',
            }}
          >
            <QRCode
              logoImage={
                productOwner?.companyLogo && `${cdnCompanyLogos}${productOwner.companyLogo}`
              }
              logoWidth={64}
              logoHeight={64}
              logoPadding={2}
              logoPaddingStyle="square"
              bgColor={'white'}
              fgColor="black"
              qrStyle={qrStyle}
              size={260}
              quietZone={10}
              removeQrCodeBehindLogo={false}
              eyeColor={[
                {
                  outer: 'black',
                  inner: corporateColor,
                },
                {
                  outer: 'black',
                  inner: corporateColor,
                },
                {
                  outer: 'black',
                  inner: corporateColor,
                },
              ]}
              value={
                product._id
                  ? `${websiteDomain}${clientRoutes.product.replace(':id', product._id as string)}`
                  : typeof window !== 'undefined'
                    ? window.location.href
                    : ''
              }
            />
          </div>

          <Group gap="md" mt={5}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'white',
                padding: '6px 14px',
                borderRadius: '8px',
                border: '1px solid #eaeaea',
                gap: '12px',
              }}
            >
              <Text size="sm" fw={500} style={{ color: '#666' }}>
                {t('marketplace.qr.style')}:
              </Text>
              <Group gap="xs">
                <Text
                  size="sm"
                  style={{
                    color: qrStyle === 'squares' ? corporateColor : '#999',
                    fontWeight: qrStyle === 'squares' ? 600 : 400,
                  }}
                >
                  {t('marketplace.qr.squares')}
                </Text>
                <Switch
                  checked={qrStyle === 'dots'}
                  onChange={(event) => setQrStyle(event.currentTarget.checked ? 'dots' : 'squares')}
                  color={corporateColor}
                  size="sm"
                />
                <Text
                  size="sm"
                  style={{
                    color: qrStyle === 'dots' ? corporateColor : '#999',
                    fontWeight: qrStyle === 'dots' ? 600 : 400,
                  }}
                >
                  {t('marketplace.qr.dots')}
                </Text>
              </Group>
            </div>
          </Group>
        </div>
      </Modal>
    </Modal>
  );
}
